import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { UserKeys, listDataTransferLogs } from '../../../api/users-api';
import { useState } from 'react';
import { DataTransferLog, Page } from '../../../../types';
import AvatarChip from '../../../shared/avatar-chip';
import { Messages } from '../../../utils/message-constants';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useUserSettings } from '../../../utils/use-user-settings';
import CalendarTransferLogsGridToolbar from './calendar-transfer-logs-grid-toolbar';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import StatusLabel, { StatusType } from '../../../shared/status-label';
import FindInPage from '@mui/icons-material/FindInPage';

export default function TransferLogsGrid() {
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
  }>({
    pageSize: 50,
    page: 0,
    cursor: null,
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      UserKeys.CalendarsTransferLogs,
      paginationModel.page,
      paginationModel.pageSize,
    ],
    queryFn: () => {
      const object: Page<DataTransferLog[]> = {
        results: [],
        firstPull: paginationModel.page === 0,
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: paginationModel.cursor,
        nameSpace: '',
      };
      if (paginationModel.page !== 0 && data && data.queries) {
        object.queries = {
          [Object.keys(data.queries)[0]]: paginationModel.cursor || '',
        };
      }
      return listDataTransferLogs(object, paginationModel.pageSize);
    },
    placeholderData: keepPreviousData,

    meta: {
      errorMessage: Messages.Errors.Network.Loading('transfer logs'),
    },
  });

  const columns: GridColDef<DataTransferLog>[] = [
    {
      field: 'dateCreated',
      headerName: 'Created Date',
      flex: 1,
      display: 'flex',
      type: 'dateTime',
      valueGetter: (value: number) => {
        const date = new Date(value);
        return date;
      },
      valueFormatter: (params: Date) => {
        return params.toLocaleString();
      },
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      flex: 1,
      display: 'flex',
      renderCell: (params) => {
        if (params.value) {
          return <AvatarChip email={params.value} type="User" />;
        }
      },
    },
    {
      field: 'srcUser',
      headerName: 'Source User',
      flex: 1,
      display: 'flex',
      renderCell: (params) => {
        if (params.value) {
          return <AvatarChip email={params.value} type="User" />;
        }
      },
    },
    {
      field: 'destUser',
      headerName: 'Destination User',
      flex: 1,
      display: 'flex',
      renderCell: (params) => {
        if (params.value) {
          return <AvatarChip email={params.value} type="User" />;
        }
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => {
        const status =
          (params.row.status
            ?.replace(/\s+/g, '')
            ?.toUpperCase() as StatusType) ?? null;
        return status ? <StatusLabel status={status} /> : null;
      },
    },
    {
      field: 'transferId',
      headerName: 'Transfer Id',
      flex: 1,
    },
  ];

  const { viewType } = useUserSettings();

  const defaultGridState = {
    sorting: {
      sortModel: [
        {
          field: 'dateCreated',
          sort: 'desc',
        },
      ],
    },
  };

  function calculateRowCount() {
    if (data && data.cursor === null) {
      return (
        data.results.length + paginationModel.page * paginationModel.pageSize
      );
    } else {
      // Fallback: just use the current rows length
      return data?.results?.length || 0;
    }
  }

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CalendarTransferLogsGridToolbar as never,
          ...createGridOverlays('Logs', <FindInPage />),
        }}
        defaultGridState={defaultGridState}
        columns={columns}
        gridKey={
          viewType.calendarTransferLogsGridStates?.activeGridState || 'default'
        }
        gridStateKey="calendarTransferLogsGridStates"
        disableRowSelectionOnClick
        getRowId={(row) => row.transferId}
        rows={data?.results || []}
        rowCount={calculateRowCount()}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          if (isFetching || isLoading) return;

          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
              cursor: null,
            });
          } else {
            setPaginationModel({
              ...newModel,
              cursor: data?.cursor || '',
            });
          }
        }}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
        }}
        loading={isLoading || isFetching}
      />
    </>
  );
}
